<template>
  <f7-block>
    <f7-block-title>{{ t('adminDashboard.avocashIdentity') }}</f7-block-title>

    <div v-if="loading" class="text-align-center" style="padding:12px 0">
      <f7-preloader size="24" />
    </div>

    <f7-block v-else-if="error" class="dash-error">
      <span>{{ error }}</span>
      <f7-button small fill @click="loadStatus">{{ t('common.retry') }}</f7-button>
    </f7-block>

    <template v-else>
      <!-- Business + channels summary -->
      <div class="metric-grid">
        <div class="metric-card">
          <span class="metric-lbl">{{ t('adminDashboard.avocashBusiness') }}</span>
          <span class="metric-val" style="font-size:14px">{{ status.business?.displayName || '-' }}</span>
        </div>
        <div class="metric-card">
          <span class="metric-lbl">{{ t('adminDashboard.avocashChannels') }}</span>
          <span class="metric-val">{{ (status.channels || []).length }}</span>
        </div>
        <div class="metric-card">
          <span class="metric-lbl">{{ t('adminDashboard.avocashSystemUsers') }}</span>
          <span class="metric-val">{{ (status.systemUsers || []).length }}</span>
        </div>
      </div>

      <!-- Provision action -->
      <div class="avocash-actions">
        <f7-button small fill icon-f7="plus_circle" @click="provision">
          {{ t('adminDashboard.avocashProvision') }}
        </f7-button>
        <f7-button small tonal icon-f7="arrow_clockwise" @click="loadStatus" />
      </div>

      <!-- System users list -->
      <f7-list v-if="(status.systemUsers || []).length" inset strong style="margin-top:12px">
        <f7-list-item
          v-for="u in status.systemUsers"
          :key="u.id"
          :title="u.name"
          :subtitle="u.userId"
          :after="u.role"
        >
          <template #media>
            <img v-if="u.avatar" :src="u.avatar" alt="" class="avocash-avatar" />
          </template>
        </f7-list-item>
      </f7-list>
    </template>
  </f7-block>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import i18n from '../../js/i18n.js';
import {
  getAvocashStatus,
  provisionAvocash,
} from '../../js/api/avocashChat.js';

const { t } = i18n.global;

const status = ref(null);
const loading = ref(false);
const error = ref(null);

async function loadStatus() {
  loading.value = true;
  error.value = null;
  try {
    status.value = await getAvocashStatus();
  } catch (err) {
    // Not provisioned yet — allow provisioning via the button.
    status.value = { business: null, channels: [], systemUsers: [] };
  } finally {
    loading.value = false;
  }
}

async function provision() {
  loading.value = true;
  error.value = null;
  try {
    status.value = await provisionAvocash({ code: 'AVOCASH', displayName: 'avocash' });
  } catch (err) {
    error.value = err?.response?.data?.message || err?.message || t('adminDashboard.avocashError');
  } finally {
    loading.value = false;
  }
}

onMounted(loadStatus);
</script>

<style scoped>
.metric-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 10px; }
.metric-card { display:flex; flex-direction:column; align-items:center; gap:4px; padding:14px 8px; border-radius:12px; background:var(--f7-list-bg-color,#f5f5f5); }
.metric-lbl { font-size:11px; color:var(--f7-on-surface-variant,#8e8e93); text-align:center; line-height:1.3; }
.metric-val { font-size:18px; font-weight:700; color:var(--f7-text-color,#333); }
.avocash-actions { display:flex; gap:8px; margin-top:12px; }
.avocash-avatar { width:32px; height:32px; border-radius:50%; object-fit:cover; }
.dash-error { display:flex; align-items:center; gap:10px; background:color-mix(in srgb, var(--f7-theme-color) 10%, transparent); border-radius:8px; padding:12px 16px; color:var(--f7-theme-color); font-size:13px; }
.dash-error .button { flex-shrink:0; }
</style>
